import { useMutation, useQuery } from '@apollo/client/react'
import { Box, IconButton } from '@chakra-ui/react'
import { Tooltip } from '@src/components/chakra-ui/tooltip'
import { AiCrmGenerateDialog } from '@src/components/sco/Admin/AiCrm/AiCrmGenerateDialog'
import { commentsForGenerate, redraftNeedsConfirm } from '@src/components/sco/Admin/AiCrm/crmComposerLogic'
import {
  chatCommentsForGenerateDialog,
  chatGenerateAccountUserId,
  chatGenerateOffered,
} from '@src/components/sco/Chat/chatGenerate'
import { useChatGenerateTools } from '@src/components/sco/Chat/useChatGenerateTools'
import { COMMENT_STORED_FILES_SELECTION } from '@src/components/sco/Comments/CommentAttachments'
import { GENERATE_CRM_REPLY } from '@src/gql/mutations/GenerateCrmReply'
import { gql } from '@src/lib/gql'
import { prettyErrorMessage } from '@src/lib/mutationErrors'
import React, { useRef, useState } from 'react'
import { LuSparkles } from 'react-icons/lu'

const GENERATE_THREAD = gql`
  query ChatGenerateThread($threadId: String) {
    chat {
      id
      thread(threadId: $threadId) {
        id
        threadId
        threadTypeKey
        userMemberships {
          id
          items {
            id
            userId
            user {
              id
              userId
              responderToolId
            }
          }
        }
      }
    }
  }
`

const GENERATE_COMMENTS = gql`
  query ChatGenerateComments(
    $collectionId: String
    $order: [[String]]
    $pageSize: Int
    $threadId: String
  ) {
    forum {
      id
      comments(collectionId: $collectionId, order: $order, pageSize: $pageSize, threadId: $threadId) {
        id
        items {
          id
          commentId
          body
          createdAt
          createdByUserId
          deletedAt
          isDraft
          withdrawnAt
          ${COMMENT_STORED_FILES_SELECTION}
        }
      }
    }
  }
`

/**
 * Generate on the member chat composer. Sco admins only, on a direct chat that has a reply
 * account. The reply is written into the box. Nothing is sent, and the rule run is left alone.
 */
export function ChatGenerateControl(props: {
  body: string
  busy: boolean
  threadId: string
  userId?: string | null
  onPending: (pending: boolean) => void
  onWrite: (markdown: string, threadId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)
  const inFlight = useRef(false)
  const [generateReply] = useMutation(GENERATE_CRM_REPLY)
  const threadQuery = useQuery(GENERATE_THREAD, {
    variables: { threadId: props.threadId },
  })
  const thread = threadQuery.data?.chat?.thread
  const accountUserId = chatGenerateAccountUserId(
    props.userId,
    (thread?.userMemberships?.items || []).map(item => ({
      responderToolId: item?.user?.responderToolId,
      userId: item?.user?.userId || item?.userId,
    }))
  )
  const offered = chatGenerateOffered({
    accountUserId,
    isScoAdmin: true,
    threadTypeKey: thread?.threadTypeKey,
  })
  const responderToolId =
    (thread?.userMemberships?.items || []).find(
      item => (item?.user?.userId || item?.userId) === accountUserId
    )?.user?.responderToolId || null
  const { tools } = useChatGenerateTools(offered, responderToolId)
  const commentsQuery = useQuery(GENERATE_COMMENTS, {
    skip: !offered,
    variables: {
      collectionId: `ChatGenerate:${props.threadId}:comments`,
      order: [['createdAt', 'DESC']],
      pageSize: 50,
      threadId: props.threadId,
    },
  })
  const comments = chatCommentsForGenerateDialog(
    commentsQuery.data?.forum?.comments?.items,
    accountUserId || ''
  )
  const sentCount = commentsForGenerate(comments, 'thread').length
  const noSentMessages = Boolean(commentsQuery.data) && sentCount === 0

  async function generate(input: {
    includeFiles: boolean
    instruction: string
    scope: 'last' | 'thread'
    toolId: string | null
  }): Promise<string | null> {
    if (inFlight.current) {
      return null
    }
    if (!accountUserId) {
      return 'Replies are off until this account has a reply tool.'
    }
    const instruction = input.instruction.trim()
    const requestedThreadId = props.threadId
    inFlight.current = true
    setPending(true)
    props.onPending(true)
    try {
      const result = await generateReply({
        variables: {
          input: {
            accountUserId,
            includeFiles: input.includeFiles,
            scope: input.scope,
            threadId: props.threadId,
            ...(instruction ? { instruction } : {}),
            ...(input.toolId ? { toolId: input.toolId } : {}),
          },
        },
      })
      const written = String(result.data?.generateCrmReply?.body || '').trim()
      if (!written) {
        return 'The model returned an empty reply.'
      }
      props.onWrite(written, requestedThreadId)
      setOpen(false)
      return null
    } catch (err) {
      return prettyErrorMessage(err)
    } finally {
      inFlight.current = false
      setPending(false)
      props.onPending(false)
    }
  }

  if (!offered) {
    return null
  }

  const generateDisabled = props.busy || pending || commentsQuery.loading || noSentMessages

  return (
    <>
      <Tooltip
        borderRadius={'6px'}
        content={'Generate'}
        fontSize={'11px'}
        fontWeight={'bold'}
        letterSpacing={'0.05em'}
        positioning={{ placement: 'top' }}
        textTransform={'uppercase'}
      >
        <Box display={'inline-flex'}>
          <IconButton
            aria-label={'Generate'}
            borderRadius={'md'}
            css={generateDisabled ? { pointerEvents: 'none' } : undefined}
            disabled={generateDisabled}
            size={'sm'}
            type={'button'}
            variant={'ghost'}
            onClick={() => {
              void commentsQuery.refetch()
              setOpen(true)
            }}
          >
            <LuSparkles size={18} />
          </IconButton>
        </Box>
      </Tooltip>
      <AiCrmGenerateDialog
        busy={props.busy || pending}
        comments={comments}
        needsConfirm={redraftNeedsConfirm({ body: props.body, suggestedBody: null })}
        open={open}
        tools={tools}
        onClose={() => setOpen(false)}
        onGenerate={generate}
      />
    </>
  )
}
